import { useState, type FormEvent } from 'react';
import { Button, Icon, Input, Logo } from '@/ds';
import { LOGO_MARK, LOGO_MARK_WHITE } from '@/ds/assets';
import { useLogin } from '@/api/hooks';
import { ApiError } from '@/api/client';
import { useControlSize } from '@/lib/useControlSize';

const PROMISES: Array<[string, string]> = [
  ['Wallet', 'Балансы сервисов с прогнозом на сколько хватит'],
  ['CheckSquare', 'Задачи команды из Notion без переключения вкладок'],
  ['BellRing', 'Предупреждение до того, как деньги закончатся'],
];

export function LoginScreen({ googleEnabled }: { googleEnabled: boolean }) {
  const login = useLogin();
  const size = useControlSize();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(!googleEnabled);

  const error = login.error instanceof ApiError ? login.error.message : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    login.mutate({ email, password });
  }

  return (
    <div className="kj-login">
      <div className="kj-login-form">
        <Logo size={28} src={LOGO_MARK} />

        <h1
          style={{
            marginTop: 'var(--space-9)',
            font: '500 38px/1.16 var(--font-display)',
            letterSpacing: '-.02em',
            color: 'var(--text-strong)',
          }}
        >
          Вход для команды KIVIN
        </h1>

        <p
          style={{
            marginTop: 'var(--space-4)',
            font: '400 15px/1.6 var(--font-text)',
            color: 'var(--text-muted)',
            maxWidth: 400,
          }}
        >
          Доступ только у трёх рабочих адресов. Если вход не проходит, напишите Артуру.
        </p>

        <div
          style={{
            marginTop: 'var(--space-8)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-5)',
            maxWidth: 400,
          }}
        >
          {googleEnabled ? (
            <>
              <Button
                size="lg"
                block
                as="a"
                href="/api/auth/google/start"
                iconLeft={<Icon name="LogIn" size={18} />}
              >
                Войти через Google
              </Button>
              {!showPasswordForm ? (
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    alignSelf: 'flex-start',
                    font: '400 13px var(--font-text)',
                    color: 'var(--text-subtle)',
                  }}
                >
                  Войти по паролю
                </button>
              ) : null}
            </>
          ) : (
            <p style={{ font: '400 13px/1.5 var(--font-text)', color: 'var(--text-subtle)' }}>
              Вход через Google пока не настроен. Работает резервный вход по паролю.
            </p>
          )}

          {showPasswordForm ? (
            <form
              onSubmit={submit}
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}
            >
              <Input
                size={size}
                label="Рабочая почта"
                placeholder="name@example.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                size={size}
                label="Пароль"
                placeholder="••••••••••••"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                {...(error ? { error } : {})}
              />
              <Button size="lg" block loading={login.isPending}>
                Войти
              </Button>
            </form>
          ) : null}
        </div>
      </div>

      <div className="kj-login-aside">
        <img
          src={LOGO_MARK_WHITE}
          alt=""
          style={{ height: 48, width: 'auto', alignSelf: 'flex-start', opacity: 0.9 }}
        />
        <div>
          <div
            style={{
              font: '600 12px var(--font-text)',
              letterSpacing: 'var(--ls-caps)',
              textTransform: 'uppercase',
              color: 'var(--rose-400)',
            }}
          >
            Внутренняя платформа
          </div>
          <div
            style={{
              marginTop: 'var(--space-5)',
              font: '500 30px/1.25 var(--font-display)',
              letterSpacing: '-.02em',
              color: 'var(--white)',
              maxWidth: 420,
            }}
          >
            Деньги, задачи и сроки на одном экране
          </div>
          <div
            style={{
              marginTop: 'var(--space-7)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
            }}
          >
            {PROMISES.map(([icon, text]) => (
              <div
                key={text}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  font: '400 14px var(--font-text)',
                  color: 'var(--rose-200)',
                }}
              >
                <Icon name={icon} size={18} color="var(--rose-400)" />
                {text}
              </div>
            ))}
          </div>
        </div>
        <div style={{ font: '400 12px var(--font-text)', color: 'var(--rose-400)', opacity: 0.8 }}>
          © KIVIN, 2026
        </div>
      </div>
    </div>
  );
}
